// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Contador/ContadorEntrar.tsx
//  Entrada do Portal do Contador (login próprio, separado do login do ERP).
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Loader2, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getApiErrorMessage } from '@/lib/apiClient';
import { sessaoContador } from '@/features/contador/contadorClient';
import { contadorService } from '@/features/contador/contadorService';
import logo from '@/assets/logo.jpg';

export default function ContadorEntrar() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [esqueci, setEsqueci] = useState(false);
  const [carregando, setCarregando] = useState(false);

  if (sessaoContador.token()) return <Navigate to="/contador" replace />;

  const entrar = async (e: React.FormEvent) => {
    e.preventDefault();
    setCarregando(true);
    try {
      const r = await contadorService.entrar(email, senha);
      sessaoContador.salvar(r.accessToken, r.usuario);
      navigate('/contador', { replace: true });
    } catch (erro) {
      toast.error('Não foi possível entrar', { description: getApiErrorMessage(erro, 'E-mail ou senha incorretos.') });
    } finally {
      setCarregando(false);
    }
  };

  const pedirLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setCarregando(true);
    try {
      await contadorService.esqueciSenha(email);
      toast.success('Pronto', { description: 'Se o e-mail estiver cadastrado, você receberá o link em instantes.' });
      setEsqueci(false);
    } catch (erro) {
      toast.error('Erro', { description: getApiErrorMessage(erro) });
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex justify-center">
          <img src={logo} alt="Udisys" className="h-14 w-auto object-contain" />
        </div>
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">Portal do Contador</CardTitle>
            <CardDescription className="text-center">
              {esqueci ? 'Informe seu e-mail para receber um link de nova senha' : 'Documentos fiscais dos seus clientes'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={esqueci ? pedirLink : entrar} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input id="email" type="email" autoComplete="username" required disabled={carregando}
                  value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              {!esqueci && (
                <div className="space-y-2">
                  <Label htmlFor="senha">Senha</Label>
                  <Input id="senha" type="password" autoComplete="current-password" required disabled={carregando}
                    value={senha} onChange={(e) => setSenha(e.target.value)} />
                </div>
              )}
              <Button type="submit" className="w-full" disabled={carregando}>
                {carregando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : !esqueci && <LogIn className="mr-2 h-4 w-4" />}
                {esqueci ? 'Enviar link' : 'Entrar'}
              </Button>
              <Button type="button" variant="link" className="w-full" onClick={() => setEsqueci(!esqueci)}>
                {esqueci ? 'Voltar para entrar' : 'Esqueci minha senha'}
              </Button>
            </form>
          </CardContent>
        </Card>
        <p className="text-center text-xs text-muted-foreground">
          Usa o Udisys Gestor na sua empresa? <Link to="/auth" className="underline">Entre por aqui</Link>.
        </p>
      </div>
    </div>
  );
}
